import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { message, Spin, Alert, Modal, Checkbox } from 'antd';
import { useNavigate } from 'react-router-dom';
import { fetchWorkspaces, selectWorkspace } from '../interfaces/authInterface';
import { setStatus, logout } from '../features/authSlice';
import { createNewWorkspace } from '../interfaces/workspaceSettingsInterface';

const WorkspaceSelector = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { workspaces, status, error } = useSelector((state) => state.auth);
  const [seedData , setSeedData] = useState(true)
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');

  useEffect(() => {
    const loadWorkspaces = async () => {
      try {
        dispatch(setStatus({ status: 'loading' }));
        await fetchWorkspaces(dispatch);
        dispatch(setStatus({ status: 'succeeded' }));
      } catch (err) {
        message.error(err.message || 'Failed to load workspaces.');
        dispatch(setStatus({ status: 'failed', error: err.message }));
      }
    };
    loadWorkspaces();
  }, []);

  const handleSelectWorkspace = async (workspaceId) => {
    try {
      dispatch(setStatus({ status: 'loading' }));
      sessionStorage.removeItem('selectedWorkspace');
      await selectWorkspace(dispatch, workspaceId);
      dispatch({ type: 'RESET_APP' });
      message.success('Workspace selected successfully!');
      navigate('/dashboard');
      dispatch(setStatus({ status: 'succeeded' }));
    } catch (err) {
      message.error(err.message || 'Failed to select workspace.');
      dispatch(setStatus({ status: 'failed', error: err.message }));
    }
  };

  const handleCreateWorkspace = async () => {
    setIsModalVisible(false);
    message.info('Workspace creation under process! Please wait until your new workspace is listed below!');
    await createNewWorkspace(dispatch, newWorkspaceName , seedData);
    await fetchWorkspaces(dispatch);
    setNewWorkspaceName('');
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getRoleStyle = (role) => {
    const r = (role || '').toLowerCase();
    if (r === 'owner') return { background: '#dbeafe', color: '#1d4ed8' };
    if (r === 'admin') return { background: '#ede9fe', color: '#6d28d9' };
    return { background: '#f1f5f9', color: '#64748b' };
  };

  if (status === 'loading' && !workspaces?.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f1f5fd]">
        <Spin size="large" tip="Loading workspaces..." />
      </div>
    );
  }

  return (
    <>
      <style>{`
        .ws-live-dot {
          display: inline-block; width: 6px; height-6px; border-radius: 50%;
          width: 6px; height: 6px;
          background: #10b981; box-shadow: 0 0 5px rgba(16,185,129,0.5);
          margin-right: 5px; vertical-align: middle;
          animation: wsPulse 2s infinite;
        }
        @keyframes wsPulse { 0%,100%{opacity:1} 50%{opacity:.35} }
        .ws-select-btn:hover { background: #2563eb !important; border-color: #2563eb !important; color: #fff !important; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(37,99,235,0.25); }
        .ws-row:hover { background: #f7f9ff; }
        .ws-logout:hover { background: #fff5f5 !important; border-color: #fca5a5 !important; color: #ef4444 !important; }
        .ws-modal-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); outline: none; }
        .ws-brand-em {
          background: linear-gradient(90deg, #2563eb, #6366f1);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        }
      `}</style>

      <div className="min-h-screen flex items-center justify-center bg-[#f1f5fd] px-4 py-10">
        <div className="w-full max-w-2xl">

          {/* Brand */}
          <div className="mb-6">
            <span className="text-xl font-extrabold tracking-tight text-[#0c1829]">
              <span className="ws-brand-em">Scale</span>Flow - Select / Create workspace to continue
            </span>
          </div>

          {/* Heading */}


          {/* Panel */}
          <div className="bg-white border border-[#dde5f4] rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(12,24,41,0.08)]">

            {/* Panel Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#e8edf7] bg-[#fafbff]">
              <div>
                <div className="text-sm font-bold text-[#0c1829]">Your Workspaces</div>
                <div className="text-xs text-[#5a6a85] mt-0.5">
                  <span className="ws-live-dot" />
                  {workspaces?.length || 0} workspace{workspaces?.length !== 1 ? 's' : ''} available
                </div>
              </div>
              <div className="flex gap-2">
                <button

                  onClick={() =>{ if(workspaces && workspaces.length >= 5){message.info("Cannot create more than 5 workspaces");return;} setIsModalVisible(true)}}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-semibold text-white border-none cursor-pointer transition-all hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg,#2563eb,#4f46e5)', boxShadow: '0 3px 10px rgba(37,99,235,0.28)' }}
                >
                  ＋ New Workspace
                </button>
                <button
                  onClick={handleLogout}
                  className="ws-logout inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-semibold text-[#5a6a85] bg-white border border-[#dde5f4] cursor-pointer transition-all"
                >
                  ↩ Logout
                </button>
              </div>

            </div>

            {status === 'failed' && (
              <div className="px-6 py-3">
                <Alert message="Error" description={error} type="error" showIcon />
              </div>
            )}

            {/* Table */}
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#f7f9ff] border-b border-[#e8edf7]">
                  <th className="px-6 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-[#93a3bb] w-[45%]">Workspace</th>
                  <th className="px-6 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-[#93a3bb] w-[35%]">Created On</th>
                  <th className="px-6 py-2.5 text-right text-[10px] font-bold uppercase tracking-widest text-[#93a3bb] w-[20%]">Action</th>
                </tr>
              </thead>
              <tbody>
                {workspaces && workspaces.map((workspace) => (
                  <tr key={workspace.id} className="ws-row border-b border-[#e8edf7] last:border-b-0 transition-colors">
                    <td className="px-6 py-3.5 align-middle">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-bold text-[#0c1829]">{workspace.name}</span>
                        {workspace.role && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold w-fit"
                            style={getRoleStyle(workspace.role)}
                          >
                            {workspace.role}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3.5 align-middle">
                      <div className="flex items-center gap-1.5 text-xs text-[#5a6a85] font-mono">
                        📅 {formatDate(workspace.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-3.5 align-middle text-right">
                      <button
                        className="ws-select-btn inline-flex items-center gap-1 px-4 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-all"
                        style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb' }}
                        onClick={() => handleSelectWorkspace(workspace.id)}
                        disabled={status === 'loading'}
                      >
                        Select →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {workspaces?.length === 0 && status !== 'loading' && (
              <div className="text-center py-12 text-[13px] text-[#93a3bb]">
                No workspaces found. Create your first workspace to get started!
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Modal */}
      <Modal
        title={<span className="text-base font-extrabold text-[#0c1829]">Create a New Workspace</span>}
        open={isModalVisible}
        onOk={handleCreateWorkspace}
        onCancel={() => { setIsModalVisible(false); setNewWorkspaceName(''); }}
        confirmLoading={status === 'loading'}
        okText="Create"
        okButtonProps={{ style: { background: 'linear-gradient(135deg,#2563eb,#4f46e5)', border: 'none', fontWeight: 700, borderRadius: 9 } }}
        cancelText="Cancel"
        style={{ top: '35%' }}
      >
        <div className="mt-4">
          <label className="block text-xs font-bold text-[#0c1829] mb-1.5">Workspace Name</label>
          <input
            className="ws-modal-input w-full px-3.5 py-2.5 bg-[#f7f9ff] border border-[#dde5f4] rounded-[10px] text-[#0c1829] text-sm transition-all"
            placeholder="e.g. Marketing Team, Dev Ops…"
            value={newWorkspaceName}
            onChange={(e) => setNewWorkspaceName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateWorkspace(); }}
          />
          <Checkbox
  className='p-2'
  id="addSampleData"
  checked={seedData}
  onChange={(e) => setSeedData(e.target.checked)}
>
  Add Sample Data <span className="text-gray-400 text-xs">(To understand the product · Can be deleted later)</span>
</Checkbox>
        </div>
      </Modal>
    </>
  );
};

export default WorkspaceSelector;
